<template>
    <TemplatesPage
        :loading="loading"
        :error-message="error"
        :info-message="info"
        :pt="{
            root: {class: 'pt-3'}
        }"
    >
        <template #header>
            <div class="flex justify-content-end mb-2">
                <TemplatesModeSelector
                    v-model:mode="isMain"
                    :options="options"
                    @update:mode="onChangeMode"
                />
            </div>
        </template>
        <div v-if="isDemo" class="grid">
            <div class="col">
                <Message severity="info" :closable="false" :pt="{
                    icon: { style: 'width: 23px; flex-shrink: 0;', class: 'mr-4' },
                    wrapper: { class: 'align-items-start' }
                }">
                    <h4 class="mb-3 mt-1 text-sm">{{ $t('lang.cluster.start.demo_title') }}</h4>
                    <span class="cluster_start__demo_desc inline-block mb-3">{{ $t('lang.cluster.start.demo_desc') }}</span>
                    <div>
                        <Button :label="$t('lang.cluster.start.demo_button')" size="small" @click="onSubscribe" />
                    </div>
                </Message>
            </div>
        </div>
        <div v-if="tableData" class="grid">
            <div class="col">
                <AdaptersDataTable :params="getTableParams" @page="update" @sort="update">
                    <Column field="name" :header="$t('lang.group.start.column_name')" :sortable="!isDemo" class="cluster_start__name_column">
                        <template #body="{ data }">
                            <div class="flex align-items-center justify-content-between flex-wrap-nowrap gap-3">
                                <div class="flex align-items-center flex-wrap-nowrap gap-2 overflow-hidden w-full">
                                    <Popper hover :content="data.name" class="max-w-full">
                                        <a
                                            class="overflow-hidden text-overflow-ellipsis white-space-nowrap block"
                                            :href="getReportUrl(data.uid, data.base)"
                                        >
                                            {{ data.name }}
                                        </a>
                                    </Popper>
                                </div>
                                <div class="flex align-items-center flex-wrap-nowrap gap-2">
                                    <i
                                        v-if="isShowOwnerEmail(data.owner)"
                                        :title="$t('lang.main.owner_email', [data.owner])"
                                        class="pi pi-users text-color-secondary text-sm"
                                    ></i>
                                    <Button
                                        v-if="data.monitoring_id"
                                        icon="pi pi-external-link"
                                        text
                                        size="small"
                                        severity="secondary"
                                        @click="onOpenMonitoring(data.monitoring_id)"
                                        :title="$t('lang.serp.list.open_monitoring')"
                                    />
                                </div>
                            </div>
                        </template>
                    </Column>
                    <Column field="create_date" :header="$t('lang.cluster.start.column_create_date')" :sortable="!isDemo" />
                    <Column field="access_date" :header="$t('lang.cluster.start.column_access_date')" :sortable="!isDemo" />
                    <Column field="base" :header="$t('lang.cluster.start.column_dbname')" :sortable="!isDemo">
                        <template #body="{ data }">
                            <span>{{ getBaseCaption(data.base) }}</span>
                        </template>
                    </Column>
                    <Column field="state" :header="$t('lang.cluster.start.column_status')" :sortable="!isDemo">
                        <template #body="{ data }">
                            <span>{{ getState(data.state) }}</span>
                        </template>
                    </Column>
                    <Column>
                        <template #body="{ data }">
                            <div class="flex justify-content-end column-gap-2">
                                <Button
                                    v-if="data.canChangeOwner"
                                    icon="pi pi-users"
                                    :class="{'opacity-50': data.monitoring_id}"
                                    size="small"
                                    severity="secondary"
                                    text
                                    :title="$t('lang.cluster.start.change_owner_report_title', [data.name])"
                                    @click="onChangeOwner(data)"
                                />
                                <Button
                                    v-if="canDelete(data)"
                                    icon="pi pi-trash"
                                    :class="{'opacity-50': data.monitoring_id}"
                                    size="small"
                                    severity="secondary"
                                    text
                                    :title="$t('lang.cluster.start.delete_report_title', [data.name])"
                                    @click="onDeleteReport(data)"
                                />
                            </div>
                        </template>
                    </Column>
                </AdaptersDataTable>
            </div>
        </div>
        <ks-delete-cluster></ks-delete-cluster>
        <ks-change-cluster-owner></ks-change-cluster-owner>
    </TemplatesPage>
</template>

<script>
import TemplatesPage from "../../../templates/Page.vue";
import KsDeleteCluster from "./KSDeleteCluster.vue";
import KsChangeClusterOwner from "./KSChangeClusterOwner.vue";
import Column from "primevue/column";
import ClusterApi from "../../../api/ClusterApi";
import {Listeners} from "../../../constants/listeners";
import {TableHelper} from "../../../helpers/TableHelper";
import Languages from "../../../mixins/Languages";
import BrowserStorage from "../../../mixins/BrowserStorage";
import Button from "primevue/button";
import AdaptersDataTable from "../../../component/adapters/DataTable.vue";
import Message from "primevue/message";
import TemplatesModeSelector from "../../../templates/ModeSelector.vue";
import {StorageKeys} from "../../../constants/storageKeys";

export default {
    name: "ClusteringStart",
    mixins: [Languages, BrowserStorage],
    props: {
        dbname: {
            type: String,
            required: true,
        },
        isDemo: {
            type: Boolean,
            required: true,
        }
    },
    data() {
        return {
            loading: false,
            error: null,
            info: null,
            tableData: null,
            sortField: 'access_date',
            rowsPerPage: TableHelper.getDefaultRowsPerPage(),
            page: 1,
            sortOrder: -1,
            first: 0,
            options: [
                {label: this.$t('lang.cluster.start.type_main'), value: true},
                {label: this.$t('lang.cluster.start.type_all'), value: false},
            ],
            isMain: true
        };
    },
    created() {
        this.isMain = this.getLocalStorageItem(StorageKeys.CHILD_PROJECTS_LIST_TYPES_MODE_SELECTOR) ?? true;
    },
    mounted() {
        this.loadData();
        this.$emitter.on(Listeners.CLUSTER_LIST_RELOAD, () => this.loadData());
    },
    methods: {
        onSubscribe() {
            window.location.href = '/' + window.locale + '/panel';
        },
        update(event) {
            this.sortField = event.sorted;
            this.sortOrder = event.sortOrder ?? this.sortOrder;
            this.rowsPerPage = event.rows;
            this.page = event.page ? event.page + 1 : 1;
            this.first = event.first;

            this.loadData();
        },
        async loadData() {
            this.loading = true;
            try {
                this.tableData = await ClusterApi.getClusterList({
                    'sort': this.sortField + '|' + this.getSortOrder,
                    'page': this.page,
                    'per_page': this.rowsPerPage,
                    'isMain': this.isMain,
                });
            } catch (e) {
                this.error = e.message
            }

            this.loading = false;
        },
        getReportUrl(uid, base) {
            return sprintf('/%s/clustering-list/%s?db=%s', window.locale, uid, base);
        },
        onChangeOwner(rowData) {
            this.$emitter.emit(Listeners.KS_CHANGE_OWNER_REPORT, rowData);
        },
        onDeleteReport(rowData) {
            this.$emitter.emit(Listeners.KS_DELETE_PROJECT, rowData);
        },
        canDelete(rowData) {
            return rowData.state === 10 || rowData.state === 2;
        },
        onChangeMode() {
            this.setLocalStorageItem(StorageKeys.CHILD_PROJECTS_LIST_TYPES_MODE_SELECTOR, this.isMain);
            this.loadData();
        },
        onOpenMonitoring(monitoringId) {
            window.open(sprintf('/%s/monitoring-list/%d', window.locale, monitoringId));
        },
        isShowOwnerEmail(owner) {
            return owner !== this.$store.getters.USER.email;
        },
    },
    computed: {
        getSortOrder() {
            return TableHelper.getEncodeSortOrder(this.sortOrder);
        },
        getTableParams() {
            return {
                value: this.tableData.data,
                rows: this.tableData.per_page,
                totalRecords: this.tableData.total,
                sortField: this.sortField,
                sortOrder: this.sortOrder,
                first: this.first,
            };
        },
    },
    components: {
        TemplatesModeSelector,
        AdaptersDataTable,
        TemplatesPage,
        KsDeleteCluster,
        KsChangeClusterOwner,
        Column,
        Button,
        Message,
    },
}
</script>

<style scoped>
.cluster_start__demo_desc,
.cluster_start__demo_content {
    font-size: 13px;
}
:deep(.cluster_start__name_column) {
    max-width: 400px;
    width: 250px;
}
</style>
